/* eslint-disable react/display-name */
import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useHistory } from 'react-router-dom';
import isMobilePhone from 'validator/lib/isMobilePhone';
import debounce from 'lodash.debounce';
import { clone } from 'ramda';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  DataGrid,
  PopupMenu,
  Drawer,
  FileUploader,
  Loader,
  Button,
  DatePicker,
  RadioButton,
  CardsGrid,
  ThemeContext,
  TextInputWithLabel,
  Toast
} from 'quantux-ui';
// Local imports
import {
  BASE_URL,
  DRAWER_BREAK_POINT,
  getHighlightedText,
  dataURItoBlob
} from '../../utils';
import { blue, gray, salmon } from '../../theme';
import CaseField from './case-field';
import CaseDrawer from './case-drawer';
import CaseUserCard from './case-user-card';
import MaskedDataInput from '../masked-data-input';
import WarningDialog from '../warning-dialog';
import CameraContainer from './camera-container';
import CropperContainer from './cropper-container';
import NewCaseDrawer from '../new-case-drawer';
import FileCard from '../file-card';
// import Superbill from '../case-dashboard/superbill';

// Icons
import CasesIcon from '../svg-icons/cases-icon';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
// Custom cropper css file
import './custom-cropper.css';

const DOCS_PER_PAGE = 50;

const DEFAULT_CELL_STYLE = {
  font: '14px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const FAX = {
  preparing: 'preparing',
  sending: 'sending'
};

const ICONS = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const HEADER_DEFS = [
  // Anesthesiologists (pin: 'left', key: 'anesthesiologists' )
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Anesthesiologists',
    field: 'anesthesiologists',
    key: 'anesthesiologists',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // CRNAs (pin: 'none', key: 'crnas')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'CRNAS',
    field: 'crnas',
    key: 'crnas',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Surgeons (pin: 'none', key: 'surgeons')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Surgeons',
    field: 'surgeons',
    key: 'surgeons',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Surgical Procedure (pin: 'none', key: 'surgicalProcedures')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Surgical Procedures',
    field: 'surgicalProcedures',
    key: 'surgicalProcedures',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 250, minWidth: 250, maxWidth: 250, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Patient Type (pin 'none', key: 'patientType')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Patient Type',
    field: 'patientType',
    key: 'patientType',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 150, minWidth: 150, maxWidth: 150, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Anesthesia Procedures (pin: 'none', key: 'anesthesiaProcedures')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Anesthesia Procedures',
    field: 'anesthesiaProcedures',
    key: 'anesthesiaProcedures',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 250, minWidth: 250, maxWidth: 250, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // ASA Score (pin 'none', key: 'asaScore')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'ASA Score',
    field: 'asaScore',
    key: 'asaScore',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Anesthesia Types (pin: 'none', key: 'anesthesiaTypes',)
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Anesthesia Position',
    field: 'anesthesiaTypes',
    key: 'anesthesiaTypes',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 189, minWidth: 189, maxWidth: 189, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  },
  // Anesthesia Modifiers (pin: 'none', key: 'anesthesiaModifiers')
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Anesthesia Modifiers',
    field: 'anesthesiaModifiers',
    key: 'anesthesiaModifiers',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    colStyles: { width: 220, minWidth: 220, maxWidth: 220, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' }
  }
];

const today = new Date();
// const isTouchDevice = true;

const CasesDataGrid = forwardRef((props, ref) => {
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [cases, setCases] = useState([]);
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [showFaxDrawer, setShowFaxDrawer] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 550);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 170);
  const [isMobileLayout, setMobileLayout] = useState(false);
  // Case drawer
  const [showCaseDrawer, setShowCaseDrawer] = useState(false);
  const [isCaseDrawerInEditMode, setCaseDrawerInEditMode] = useState(false);
  // Case upload dialog
  const [showCaseUploadDrawer, setShowCaseUploadDrawer] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isAnalyzingForm, setIsAnalyzingForm] = useState(false);
  const [formS3Key, setFormS3Key] = useState('');
  const [analyzeFaceSheet, setAnalyzeFaceSheet] = useState(false);
  // Case analysis drawer
  const [showCaseAnalysisDrawer, toggleShowCaseAnalysisDrawer] = useState(false);
  const [caseDosDate, setCaseDosDate] = useState(today.getDate());
  const [caseDosMonth, setCaseDosMonth] = useState(today.getMonth());
  const [caseDosYear, setCaseDosYear] = useState(today.getFullYear());
  const [casePatientType, setCasePatientType] = useState('');
  // Common
  const [caseData, setCaseData] = useState({});
  const [showCardsGrid, setShowCardsGrid] = useState(false);
  // Case anlysis - new UI
  const [currentInput, setCurrentInput] = useState('Facilities');
  const [searchTerm, setSearchTerm] = useState('');
  const [facilityList, setFacilityList] = useState([]);
  const [surgeonsList, setSurgeonsList] = useState([]);
  const [proceduresList, setProceduresList] = useState([]);
  const [anesthesiologistsList, setAnesthesiologistsList] = useState([]);
  const [crnasList, setCrnasList] = useState([]);
  const [isNoneAnesthesiologist, setIsNoneAnesthesiologist] = useState(false);
  const [isNoneCrna, setIsNoneCrna] = useState(false);
  const [canCreateCase, setCanCreateCase] = useState(false);
  //
  const [showFacilityDataInput, setShowFacilityDataInput] = useState(false);
  const [showSurgeonsDataInput, setShowSurgeonsDataInput] = useState(false);
  const [showProceduresDataInput, setShowProceduresDataInput] = useState(false);
  const [showAnesthesiologistsDataInput, setShowAnesthesiologistsDataInput] = useState(false);
  const [showCrnasDataInput, setShowCrnasDataInput] = useState(false);
  // Camera
  const [showCameraDrawer, setShowCameraDrawer] = useState(false); // original value => false
  const [showCropper, setShowCropper] = useState(false); // original value => false
  // Cropper Container -> Camera Container
  const [data, setData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  // Warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  // Edit last case
  const [editLastCase, setEditLastCase] = useState(false);
  // Fax Drawer
  const [recipientName, setRecipientName] = useState('');
  const [recipientFax, setRecipientFax] = useState('');
  const [isRecipientFaxPristine, setIsRecipientFaxPristine] = useState(true);
  const [faxCoverMessage, setFaxCoverMessage] = useState('');
  // const [showSuperBill, setShowSuperBill] = useState(false);
  const [canSendFax, setCanSendFax] = useState(false);
  const [showFaxLoader, setShowFaxLoader] = useState(false);
  const [faxLoaderMessage, setFaxLoaderMessage] = useState('Sending Fax');
  const [faxStatus, setFaxStatus] = useState(FAX.sending);
  const [showFaxDocs, setShowFaxDocs] = useState(false);
  const [faxData, setFaxData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [showFaxDocsThumbnailDrawer, setShowFaxDocsThumbnailDrawer] = useState(false);
  const [faxDocs, setFaxDocs] = useState([]);
  const [selectedFaxDocument, setSelectedFaxDocument] = useState(0);
  // --
  const [faxStatusMsg, setFaxStatusMsg] = useState('');
  const [showFaxStatusMsg, setShowFaxStatusMsg] = useState(false);
  // PDF Download
  const [isDownloadingCaseDocs, setIsDownloadingCaseDocs] = useState(false);
  // Warning Dialog
  const [showDeleteCaseWarning, setShowDeleteCaseWarning] = useState(false);

  // Router History
  const history = useHistory();

  // New case drawer
  const [showNewCaseDrawer, setShowNewCaseDrawer] = useState(false);

  // Refs
  const gridParentRef = useRef();
  const gridEllipsisMenu = useRef();
  const fileInputRef = useRef();
  const camera = useRef(null);
  const imgCropper = useRef();

  // Context - Touch device settings
  const context = useContext(ThemeContext);
  const { isTouchDevice } = context.theme;

  // Drawer width
  let drawerWidth = window.innerWidth <= 700 ? window.innerWidth : 375;
  if (isTouchDevice && showCameraDrawer) {
    drawerWidth = window.innerWidth;
  }

  // ------------------------------ Styles ------------------------------
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0',
      paddingRight: '8px'
      // marginTop: '8px'
    },
    gridContainer: {
      display: (showCaseDrawer || showCaseUploadDrawer || showCaseAnalysisDrawer) ? 'none' : 'block',
      width: `${gridWidth}px`,
      overflow: 'hidden',
      height: showCardsGrid ? `${window.innerHeight - 134}px` : ''
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    drawerContainer: {
      background: gray[800],
      color: gray[300],
      // height: '100%',
      height: `${window.innerHeight}px`,
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: `1px solid ${gray[600]}`,
      flexShrink: '0',
      background: gray[600]
    },
    headerSvgIcon: {
      fill: gray[300],
      marginRight: '8px',
      height: '20px'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerContentArea: {
      overflowY: 'auto',
      display: 'flex',
      color: gray[300],
      // flexGrow: '1',
      flexFlow: 'column',
      height: 'calc(100% - 80px)'
    },
    faxDocsDrawerContentArea: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'calc(100vh - 136px)',
      // height: 'calc(100vh - 80px)',
      overflowY: 'hidden',
      color: gray[300]
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-start',
      background: gray[700],
      height: '48px',
      minHeight: '48px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    faxDocsDrawerFooter: {
      display: 'flex',
      paddingRight: '8px',
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: blue[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    headerSection: {
      minWidth: '75px',
      textAlign: 'right',
      fontSize: '12px',
      fontWeight: '700'
    },
    headerSectionText: {
      marginLeft: '4px',
      color: 'white',
      fontSize: '12px',
      fontWeight: '700',
      textTransform: 'capitalize'
    },
    caseFieldCheckbox: {
      height: '14px',
      width: '14px',
      marginLeft: '0'
    },
    menuItem: {
      cursor: 'pointer',
      background: blue[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      borderBottom: `1px solid ${gray[600]}`,
      fontSize: '14px',
      // height: '40px',
      ':hover': {
        background: gray[500],
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    menuItemAvatarContainer: {
      width: '32px',
      height: '32px'
    },
    menuItemAvatarImg: {
      // width: 'auto',
      width: '32px',
      height: '32px',
      maxHeight: '32px',
      borderRadius: '50%'
    },
    searchMenu: {
      background: blue[900],
      borderRadius: '0 5px',
      paddingBottom: '1px'
    },
    faxCoverMessage: {
      fontFamily: 'Lato',
      fontSize: '16px',
      height: window.innerWidth <= 700 ? '-webkit-fill-available' : '100%',
      width: '100%',
      padding: '5px',
      ':focus': {
        outline: 'none'
      }
    },
    faxDocsToolbar: {
      height: '40px',
      background: gray[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px'
    },
    faxDocsToolbarLeft: {
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    badge: {
      position: 'absolute',
      top: '18px',
      right: '0px',
      padding: '0 4px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: blue[500],
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer'
    },
    toolbarIcon: {
      fontSize: '22px',
      color: gray[300],
      fill: '#96C8FA',
      cursor: 'pointer',
      ':hover': {
        fill: blue[400],
        color: blue[400]
      }
    },
    pageSelector: {
      display: 'flex',
      height: '30px',
      width: '120px',
      border: `1px solid ${gray[800]}`,
      borderRadius: '5px',
      marginLeft: '16px'
    },
    pageSelectorBtn: {
      fontSize: '12px',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: blue[800]
      // background: '#083050'
    },
    pageSelectorPageNumberDisplay: {
      width: '80x',
      background: 'black',
      color: gray[300],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: '1',
      height: '30px'
    },
    thumbnailDrawer: {
      display: 'flex',
      flexFlow: 'column',
      position: 'absolute',
      width: '124px',
      overflowY: 'auto',
      background: blue[900],
      borderRight: `1px solid ${blue[600]}`
    },
    faxDocImgAutosize: {
      maxHeight: '95%',
      maxWidth: '95%'
    }
  });

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      // console.log(`---- gridParentRefWidth -------> ${gridParentRefWidth}`);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : gridParentRefWidth;
      setGridWidth(Math.min(tmp, 1780));
      setGridHeight(window.innerHeight - 150);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  const getCaseData = () => {
    const formData = new FormData();
    formData.append('formKey', formS3Key);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/getCaseFormData`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          console.log('------------------ Case Data ------------------');
          console.log(res.newCase);
          const { facility, surgeons } = res.newCase;
          if (res.newCase.facility) {
            setFacilityList((prevState) => {
              return prevState.concat({
                id: facility.id,
                facility: facility.facility,
                facilityName: facility.facilityName,
                npi: facility.npi,
                text: `${facility.facilityName}`,
                icon: facility.avatarImageUrl,
                user: facility.id,
                fullName: facility.facilityName,
                caseRelation: ''
              });
            });
          }

          if (surgeons.length > 0) {
            surgeons.forEach((el) => {
              setSurgeonsList((prevState) => {
                return prevState.concat({
                  id: el._id,
                  text: `${el.lastName}, ${el.firstName}`,
                  icon: el.avatarImageUrl,
                  user: el._id,
                  fullName: el.fullName,
                  npi: el.npi,
                  caseRelation: 'Surgeon'
                });
              });
            });
          }

          if (res.newCase.dateOfSurgery && res.newCase.dateOfSurgery.length > 0) {
            const surgeryDate = new Date(Date.parse(res.newCase.dateOfSurgery)) || new Date();
            setCaseDosDate(surgeryDate.getDate());
            setCaseDosMonth(surgeryDate.getMonth());
            setCaseDosYear(surgeryDate.getFullYear());
          }

          setShowCaseUploadDrawer(false);
          setIsAnalyzingForm(false);
          toggleShowCaseAnalysisDrawer(true);
        } else {
          console.log('Cannot read form');
        }
      })
      .catch(error => console.log('error', error));
  };

  const searchFacilities = (value) => {
    const url = new URL(`${BASE_URL}/users/searchFacilities`);
    // const params = { searchTerm: facilityName.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchFacilities = useCallback(
    debounce((searchTerm) => {
      searchFacilities(searchTerm);
    }, 500), []);

  const searchSurgeonsList = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedPros`);
    // const params = { searchTerm: surgeonsListSearchTerm.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchSurgeonsList = useCallback(
    debounce((searchTerm) => {
      searchSurgeonsList(searchTerm);
    }, 500), []
  );

  const searchProceduresList = (value) => {
    const url = new URL(`${BASE_URL}/cpt/searchCpt`);
    // const params = { searchTerm: proceduresListSearchTerm.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchProceduresList = useCallback(
    debounce((searchTerm) => {
      searchProceduresList(searchTerm);
    }, 500), []
  );

  const searchAnesthesiologistsList = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedPros`);
    // const params = { searchTerm: anesthesiologistsListSearchTerm.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchAnesthesiologistsList = useCallback(
    debounce((searchTerm) => {
      searchAnesthesiologistsList(searchTerm);
    }, 500), []
  );

  const searchCrnasList = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedPros`);
    // const params = { searchTerm: crnasListSearchTerm.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchCrnasList = useCallback(
    debounce((searchTerm) => {
      searchCrnasList(searchTerm);
    }, 500), []
  );

  useImperativeHandle(ref, () => {
    const resizeGrid = handleWindowResize;
    return { resizeGrid };
  });

  const dealyedHandleWindowResize = () => { setTimeout(handleWindowResize, 500); };

  // ------------------------------ Effects ------------------------------
  useEffect(() => {
    // window.addEventListener('resize', handleWindowResize);
    window.addEventListener('resize', dealyedHandleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      // window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('resize', dealyedHandleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (analyzeFaceSheet) {
      getCaseData();
    }
  }, [formS3Key]);

  useEffect(() => {
    switch (currentInput) {
      case 'Surgeon(s)': {
        if (searchTerm.trim().length > 0) {
          debouncedSearchSurgeonsList(searchTerm);
        }
        break;
      }
      case 'Procedure(s)': {
        if (searchTerm.trim().length > 0) {
          debouncedSearchProceduresList(searchTerm);
        }
        break;
      }
      case 'Anesthesiologist(s)': {
        if (searchTerm.trim().length > 0) {
          debouncedSearchAnesthesiologistsList(searchTerm);
        }
        break;
      }
      case 'CRNA(s)/ CAA(s)': {
        if (searchTerm.trim().length > 0) {
          debouncedSearchCrnasList(searchTerm);
        }
        break;
      }
      case 'Facilities':
      default: {
        if (searchTerm.trim().length > 0) {
          debouncedSearchFacilities(searchTerm);
        }
        break;
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    // console.log('[Debug] filters hook');
    getData();
  }, [currentPage]);

  useEffect(() => {
    const casePatientTypeFlag = casePatientType === 'InPt' || casePatientType === 'OutPt';
    const facilityFlag = facilityList.length > 0;
    // const surgeonsFlag = surgeonsList.length > 0;
    const proceduresFlag = proceduresList.length > 0;
    const anesthesiologistsFlag = isNoneAnesthesiologist || anesthesiologistsList.length > 0;
    const crnaFlag = isNoneCrna || crnasList.length > 0;
    setCanCreateCase(
      casePatientTypeFlag &&
      facilityFlag &&
      // surgeonsFlag &&
      proceduresFlag &&
      anesthesiologistsFlag &&
      crnaFlag
    );
  }, [
    casePatientType,
    facilityList,
    surgeonsList,
    proceduresList,
    anesthesiologistsList,
    crnasList,
    isNoneAnesthesiologist,
    isNoneCrna
  ]);

  useEffect(() => {
    if (editLastCase) {
      setEditLastCase(false);
      setPopupMenuIndex(cases.length - 1);
      setCaseData(cases[cases.length - 1]);
      setCaseDrawerInEditMode(true);
      setShowCaseDrawer(true);
    }
  }, [editLastCase]);

  useEffect(() => {
    if (recipientName.length > 0 && recipientFax.length > 0 && isMobilePhone(recipientFax, 'en-US')) {
      setCanSendFax(true);
    }
  }, [recipientName, recipientFax]);

  const getData = async (openLastCase = false) => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    // formData.append('filters', JSON.stringify(filters));
    // formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/cases/all`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/cases/allCount`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting users grid data and count');
        console.log(err);
      });

    if (response) {
      const casesDataResponse = await response[0].json();
      const casesCountResponse = await response[1].json();

      if (casesDataResponse && casesDataResponse.success) {
        const data = [];
        for (let i = 0; i < casesDataResponse.cases.length; i++) {
          const activeRecord = casesDataResponse.cases[i];
          let anesthesiologists = '';
          let crnas = '';
          let surgeons = '';
          let facility = '';
          let surgicalProcedures = '';
          let anesthesiaProcedures = '';
          let anesthesiaTypes = '';
          let anesthesiaModifiers = '';

          // if (i === casesDataResponse.cases.length - 1) {
          //   console.log(activeRecord);
          // }

          if (activeRecord.anesthesiologists.length > 0) {
            activeRecord.anesthesiologists.forEach(el => { anesthesiologists += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.otherAnesthesiologists && activeRecord.otherAnesthesiologists.length > 0) {
            activeRecord.otherAnesthesiologists.forEach(el => { anesthesiologists += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.crnas.length > 0) {
            activeRecord.crnas.forEach(el => { crnas += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.otherCrnas && activeRecord.otherCrnas.length > 0) {
            activeRecord.otherCrnas.forEach(el => { crnas += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.surgeons.length > 0) {
            activeRecord.surgeons.forEach(el => { surgeons += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.otherSurgeons && activeRecord.otherSurgeons.length > 0) {
            activeRecord.otherSurgeons.forEach(el => { surgeons += `${el.fullName}` + '\n'; });
          }

          if (activeRecord.surgicalProcedures.length > 0) {
            activeRecord.surgicalProcedures.forEach(el => { surgicalProcedures += `${el.description}` + '\n'; });
          }

          if (activeRecord.otherSurgicalProcedures && activeRecord.otherSurgicalProcedures.length > 0) {
            activeRecord.otherSurgicalProcedures.forEach(el => { surgicalProcedures += `${el.description}` + '\n'; });
          }

          if (anesthesiaProcedures.length > 0) {
            activeRecord.anesthesiaProcedures.forEach(el => { anesthesiaProcedures += `${el.description}` + '\n'; });
          }

          if (activeRecord.otherAnesthesiaProcedures && activeRecord.otherAnesthesiaProcedures.length > 0) {
            activeRecord.otherAnesthesiaProcedures.forEach(el => { anesthesiaProcedures += `${el.description}` + '\n'; });
          }

          activeRecord.anesthesiaPositions.forEach(el => { anesthesiaTypes += el + '\n'; });
          activeRecord.anesthesiaModifiers.forEach(el => { anesthesiaModifiers += el + '\n'; });

          if (activeRecord.isOtherSurgeon) {
            surgeons += 'Other';
          }

          if (activeRecord.otherFacility) {
            facility = activeRecord.otherFacility.facilityName;
          } else {
            facility = activeRecord.facility ? activeRecord.facility.facilityName : 'None';
          }

          if (activeRecord.isOtherAnesthesiologist) {
            anesthesiologists += 'Other';
          }

          if (activeRecord.isNoneAnesthesiologist) {
            anesthesiologists += 'None';
          }

          if (activeRecord.isOtherCrna) {
            crnas += 'Other';
          }

          if (activeRecord.isNoneCrna) {
            crnas += 'None';
          }

          if (activeRecord.isOtherSurgicalProcedure) {
            surgicalProcedures += 'Other';
          }

          if (activeRecord.anesthesiaProcedures) {
            anesthesiaProcedures += 'Other';
          }

          const pDob = new Date(Date.parse(activeRecord.patientDob));
          const patientAge = (new Date().getTime() - pDob.getTime()) / (365 * 24 * 60 * 60 * 1000);
          // const pSummary = `${activeRecord.patientLastName}, ${activeRecord.patientFirstName} | ${patientAge}Y | ${pDob.toLocaleDateString()}`;
          data.push({
            id: activeRecord._id,
            anesthesiologists: anesthesiologists.trim(),
            crnas: crnas.trim(),
            surgeons: surgeons.trim(),
            surgicalProcedures: surgicalProcedures.trim(),
            patientType: activeRecord.patientType,
            anesthesiaProcedures: anesthesiaProcedures.trim(),
            asaScore: activeRecord.asaScore,
            anesthesiaTypes: anesthesiaTypes.trim(),
            anesthesiaModifiers: anesthesiaModifiers.trim(),
            facilityName: facility,
            patientSummary: `${activeRecord.patientLastName}, ${activeRecord.patientFirstName} | ${patientAge}Y | ${pDob.toLocaleDateString()}`,
            dos: new Date(Date.parse(activeRecord.dateOfSurgery)),
            faceSheetImageData: activeRecord.faceSheetImageData,
            consentFormImageData: activeRecord.consentFormImageData,
            preOpFormImageData: activeRecord.preOpFormImageData,
            intraOpFormImageData: activeRecord.intraOpFormImageData,
            postOpFormImageData: activeRecord.postOpFormImageData
            // lastUpdate: activeRecord.createdOn ? new Date(activeRecord.createdOn).toLocaleDateString() : (new Date()).toLocaleDateString()
          });
        }
        setGridData(data);
        setShowLoader(false);
        setCases(casesDataResponse.cases);
        if (openLastCase) {
          setEditLastCase(true);
        }
      }

      if (casesCountResponse && casesCountResponse.success) {
        setDocsCount(casesCountResponse.casesCount);
      }
    }
  };

  const handleOnClickToggleGrid = () => {
    setShowCardsGrid(!showCardsGrid);
  };

  const handleOnClickResetFilters = () => { };

  const handleOnClickCell = (col, _, recordId) => {
    if (col === 'anesthesiologists') {
      const caseId = recordId;
      history.push(`/case/${caseId}/manage/faceSheet`);
    }
  };

  const handleOnPopupMenu = (e, idx) => {
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickEditCaseOldDashboard = () => {
    // setCaseDrawerInEditMode(true);
    // setShowCaseDrawer(true);
    // console.log(cases[popupMenuIndex]);
    // setCaseData(cases[popupMenuIndex]);
    history.push(`/case2/${cases[popupMenuIndex]._id}`);
  };

  const handleOnClickFaxCase = () => {
    const tmpFaxDocs = [];
    // FaceSheet
    if (cases[popupMenuIndex].faceSheetDocs.length > 0) {
      cases[popupMenuIndex].faceSheetDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Records Labs Docs
    if (cases[popupMenuIndex].labsDocs.length > 0) {
      cases[popupMenuIndex].labsDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Records EKG Docs
    if (cases[popupMenuIndex].ekgDocs.length > 0) {
      cases[popupMenuIndex].ekgDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Records Imaging Docs
    if (cases[popupMenuIndex].imagingDocs.length > 0) {
      cases[popupMenuIndex].imagingDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Records Consults Docs
    if (cases[popupMenuIndex].consultsDocs.length > 0) {
      cases[popupMenuIndex].consultsDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Anesthesia Consent Form
    if (cases[popupMenuIndex].consentFormDocs.length > 0) {
      cases[popupMenuIndex].consentFormDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Anesthesia Pre Op Form
    if (cases[popupMenuIndex].preOpFormDocs.length > 0) {
      cases[popupMenuIndex].preOpFormDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Anesthesia Intra Op Form
    if (cases[popupMenuIndex].intraOpFormDocs.length > 0) {
      cases[popupMenuIndex].intraOpFormDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // Anesthesia Post Op Form
    if (cases[popupMenuIndex].postOpFormDocs.length > 0) {
      cases[popupMenuIndex].postOpFormDocs.forEach(el => tmpFaxDocs.push(el));
    }
    // console.log(tmpFaxDocs);
    setFaxDocs(tmpFaxDocs);
    setIsRecipientFaxPristine(true);
    setShowFaxStatusMsg(false);
    setRecipientName('');
    setRecipientFax('');
    setFaxCoverMessage('');
    setShowFaxDrawer(true);
  };

  const downloadFile = (href, download) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = download;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOnClickDeleteCase = () => {
    const formData = new FormData();
    formData.append('caseId', cases[popupMenuIndex]._id);

    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/cases/deleteCase`, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          setShowDeleteCaseWarning(false);
          getData();
        }
      })
      .catch((err) => console.log(err));
  };

  const handleOnClickDownloadCase = () => {
    setIsDownloadingCaseDocs(true);
    toggleGridEllipsisMenu(false);

    const formData = new FormData();
    formData.append('caseId', cases[popupMenuIndex]._id);

    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/cases/downloadCaseDocs`, requestOptions)
      .then(response => response.blob())
      .then((data) => {
        setIsDownloadingCaseDocs(false);
        let fileName = 'caseData.pdf';
        console.log(cases[popupMenuIndex]);
        toggleGridEllipsisMenu(false);
        // Anesthesiologist
        let anesthesiologistInitials = '';
        if (cases[popupMenuIndex].anesthesiologists.length > 0) {
          const tmp = cases[popupMenuIndex].anesthesiologists[0].fullName.split(', ');
          anesthesiologistInitials = `${tmp[1].charAt(0)}${tmp[0]}`;
        }

        if (cases[popupMenuIndex].anesthesiaPractices.length > 0) {
          if (cases[popupMenuIndex].anesthesiaPractices[0].addedProviders.length > 0) {
            anesthesiologistInitials = `${cases[popupMenuIndex].anesthesiaPractices[0].addedProviders[0].providerFirstName.charAt(0)}${cases[popupMenuIndex].anesthesiaPractices[0].addedProviders[0].providerLastName}`;
          }
        }

        // Date of Surgery
        let dos = '';
        if (cases[popupMenuIndex].dateOfSurgery.length > 0) {
          const tmp = cases[popupMenuIndex].dateOfSurgery.split('T');
          const dateArr = tmp[0].split('-');
          const month = dateArr[1];
          const date = dateArr[2];
          const year = +dateArr[0] - 2000;
          dos = `${month}-${date}-${year}`;
        }

        // Facility UserName
        let facilityUserName = '';
        if (cases[popupMenuIndex].facility && Object.keys(cases[popupMenuIndex].facility).length > 0) {
          facilityUserName = cases[popupMenuIndex].facility.facilityUserName || '';
        }

        // Anesthesia Start and End Times
        const startHour = cases[popupMenuIndex].anesthesiaStartHour < 10 ? `0${cases[popupMenuIndex].anesthesiaStartHour}` : cases[popupMenuIndex].anesthesiaStartHour;
        const startMinute = cases[popupMenuIndex].anesthesiaStartMinute < 10 ? `0${cases[popupMenuIndex].anesthesiaStartMinute}` : cases[popupMenuIndex].anesthesiaStartMinute;
        const endHour = cases[popupMenuIndex].anesthesiaEndHour < 10 ? `0${cases[popupMenuIndex].anesthesiaEndHour}` : cases[popupMenuIndex].anesthesiaEndHour;
        const endMinute = cases[popupMenuIndex].anesthesiaEndMinute < 10 ? `0${cases[popupMenuIndex].anesthesiaEndMinute}` : cases[popupMenuIndex].anesthesiaEndMinute;
        const times = `${startHour}${startMinute}-${endHour}${endMinute}`;

        // Filename
        fileName = `${anesthesiologistInitials}_${dos}_@${facilityUserName}_${times}`;

        downloadFile(window.URL.createObjectURL(data), `${fileName}.pdf`);
      })
      .catch((err) => console.log(err));
  };

  const uploadCase = () => {
    const formData = new FormData();
    const img = fileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadPrivateFile`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setFormS3Key(res.fileName);
          setIsUploadingFile(false);
          setIsAnalyzingForm(true);
          // setShowCaseUploadDrawer(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsUploadingFile(true);
    fetch(`${BASE_URL}/users/uploadPrivateFile`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setFormS3Key(res.fileName);
          setIsUploadingFile(false);
          setIsAnalyzingForm(true);
          // setShowCaseUploadDrawer(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFileSelected = () => {
    if (fileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', bannerFileInputRef.current.files[0]);
      // console.log('typeof img', typeof bannerFileInputRef.current.files[0]);
      setIsUploadingFile(true);
      uploadCase();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnUploadFaceSheet = (fileName) => {
    setAnalyzeFaceSheet(false);
    if (!isCaseDrawerInEditMode) {
      setFormS3Key(fileName);
    } else {
      setCaseData({
        ...caseData,
        faceSheetKey: fileName
      });
    }
  };

  const handleOnDeleteFaceSheet = () => {
    if (!isCaseDrawerInEditMode) {
      setFormS3Key('');
    } else {
      setCaseData({
        ...caseData,
        faceSheetKey: ''
      });
    }
  };

  // Redundant previous code for masked input deleted !

  const facilityCards = [];
  if (facilityList.length > 0) {
    facilityList.forEach((el) => {
      facilityCards.push(
        <CaseUserCard
          key={el.id}
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          dataId={el.id}
          onClose={(id) => {
            setFacilityList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const surgeonCards = [];
  if (surgeonsList.length > 0) {
    surgeonsList.forEach((el) => {
      surgeonCards.push(
        <CaseUserCard
          key={el.id}
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          dataId={el.id}
          onClose={(id) => {
            setSurgeonsList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const surgicalProcedureCards = [];
  if (proceduresList.length > 0) {
    proceduresList.forEach((el) => {
      surgicalProcedureCards.push(
        <CaseUserCard
          key={el.id}
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          noAvatar
          dataId={el.id}
          onClose={(id) => {
            setProceduresList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{`${el.description} (${el.code})`}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const anesthesiologistCards = [];
  if (anesthesiologistsList.length > 0) {
    anesthesiologistsList.forEach((el) => {
      anesthesiologistCards.push(
        <CaseUserCard
          key={el.id}
          className='mt-1'
          avatarImageUrl={el.icon}
          iconClass={el.iconClass}
          showCloseIcon
          dataId={el.id}
          onClose={(id) => {
            setAnesthesiologistsList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const crnaCards = [];
  if (crnasList.length > 0) {
    crnasList.forEach((el) => {
      crnaCards.push(
        <CaseUserCard
          key={el.id}
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          iconClass={el.iconClass}
          dataId={el.id}
          onClose={(id) => {
            setCrnasList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const resetCaseAnalysisDrawerState = () => {
    setCaseDosDate(today.getDate());
    setCaseDosMonth(today.getMonth());
    setCaseDosYear(today.getFullYear());
    setCasePatientType('');
    setCurrentInput('Facilities');
    setSearchTerm('');
    setShowFacilityDataInput(false);
    setShowSurgeonsDataInput(false);
    setShowProceduresDataInput(false);
    setShowAnesthesiologistsDataInput(false);
    setShowCrnasDataInput(false);
    setFacilityList([]);
    setSurgeonsList([]);
    setProceduresList([]);
    setAnesthesiologistsList([]);
    setCrnasList([]);
    setIsNoneAnesthesiologist(false);
    setIsNoneCrna(false);
  };

  const closeCaseAnalysisDrawer = () => {
    toggleShowCaseAnalysisDrawer(false);
    resetCaseAnalysisDrawerState();
  };

  const handleOnClickCreateCase = () => {
    setShowCaseUploadDrawer(false);
    toggleShowCaseAnalysisDrawer(false);
    setShowLoader(true);
    // setShowCaseDrawer(true);

    // Saving initial case
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('dateOfSurgery', new Date(caseDosYear, caseDosMonth, caseDosDate));
    formData.append('patientType', casePatientType);
    formData.append('facility', JSON.stringify(facilityList[0]));
    formData.append('isOtherFacility', (facilityList.findIndex(el => el.npi === 0)) >= 0);

    formData.append('surgeons', JSON.stringify(surgeonsList.filter(el => el.npi !== 0)));
    formData.append('otherSurgeons', JSON.stringify(surgeonsList.filter(el => el.npi === 0)));

    formData.append('surgicalProcedures', JSON.stringify(proceduresList.filter(el => el.npi !== 0)));
    formData.append('otherSurgicalProcedures', JSON.stringify(proceduresList.filter(el => el.npi === 0)));

    formData.append('anesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi === 0)));

    formData.append('crnas', JSON.stringify(crnasList.filter(el => el.npi !== 0)));
    formData.append('otherCrnas', JSON.stringify(crnasList.filter(el => el.npi === 0)));

    formData.append('faceSheetKey', formS3Key);

    formData.append('isOtherSurgeon', (surgeonsList.findIndex(el => el.npi === 0)) >= 0);
    formData.append('isOtherProcedure', (proceduresList.findIndex(el => el.npi === 0)) >= 0);
    formData.append('isOtherAnesthesiologist', (anesthesiologistsList.findIndex(el => el.npi === 0)) >= 0);
    formData.append('isOtherCrna', (crnasList.findIndex(el => el.npi === 0)) >= 0);
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));
    // Images Data
    formData.append('imageData', new Blob([JSON.stringify(data)], { type: 'text/plain' }));

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/cases/createCase`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData(true);
        }
      });
  };

  const closeCameraDrawer = () => {
    setShowCropper(false);
    setShowCameraDrawer(false);
    setData((prvState) => ({
      ...data,
      images: []
    }));
  };

  const createPdf = (images) => {
    const formData = new FormData();
    images.forEach((el, idx) => {
      formData.append('images', dataURItoBlob(el));
    });

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const options = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/createPdf`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setFormS3Key(res.fileName);
          setIsUploadingFile(false);
          setShowCameraDrawer(false);
          setShowCropper(false);
          setIsAnalyzingForm(false);
          setShowCaseUploadDrawer(false);
          setShowCaseDrawer(false);
          resetCaseAnalysisDrawerState();
          toggleShowCaseAnalysisDrawer(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickCropAccept = () => {
    setShowCameraDrawer(false);
    setShowCaseUploadDrawer(true);
    setIsAnalyzingForm(false);
    setIsUploadingFile(true);
    // uploadCroppedFaceSheet(blob);
    if (data.croppedImages.length > 0) {
      createPdf(data.croppedImages);
    }
  };

  const handleOnEditRecipientName = (e) => {
    const { value } = e.target;
    setRecipientName(value);
  };

  const handleOnEditRecipientFax = (e) => {
    const { value } = e.target;
    setIsRecipientFaxPristine(false);
    setRecipientFax(value);
  };

  const handleOnEditFaxCoverMessage = (e) => {
    const { value } = e.target;
    setFaxCoverMessage(value);
  };

  const handleOnClickSendFax = async () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('caseId', cases[popupMenuIndex]._id);
    formData.append('sendersName', recipientName);
    formData.append('sendersFax', recipientFax);
    formData.append('faxCoverMessage', faxCoverMessage);
    formData.append('faxDocs', JSON.stringify(faxDocs));

    // let sBillImg = null;
    // /* eslint-disable */
    // // const superBillCanvas = await html2canvas(document.getElementById("sbill"))
    // // sBillImg = superBillCanvas.toDataURL();
    // /* eslint-enable */

    // formData.append('sBillData', new Blob(
    //   [sBillImg],
    //   // { type: 'text/plain' }
    //   { type: 'image/png' }
    // ));

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/cases/sendFax`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setShowFaxStatusMsg(true);
          setFaxStatusMsg('Fax sent successfully!');
          setTimeout(() => {
            setShowFaxStatusMsg(false);
          }, 3000);
          setShowFaxDrawer(false);
          setShowFaxDocs(false);
        } else {
          setShowFaxStatusMsg(true);
          setFaxStatusMsg('Error sending fax!');
          setTimeout(() => {
            setShowFaxStatusMsg(false);
          }, 3000);
        }
      });
  };

  const handleOnClickViewFaxDocs = async () => {
    const updatedFaxData = clone(faxData);

    // Set loading state for fax docs drawer
    setShowFaxLoader(true);
    setFaxLoaderMessage('Preparing Fax');
    setFaxStatus(FAX.preparing);
    setShowFaxDocs(true);
    // Load case docs
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    const formData = new FormData();
    const requestOptions = { method: 'POST', headers: myHeaders, body: formData, redirect: 'follow' };

    formData.append('caseId', cases[popupMenuIndex]._id);
    const result = await fetch(`${BASE_URL}/cases/getCaseDocs`, requestOptions).catch(err => console.log(err));
    const res = await result.json();
    if (res.success) {
      if (res.caseDocs.faceSheetData) {
        updatedFaxData.images = updatedFaxData.images.concat(res.caseDocs.faceSheetData);
        updatedFaxData.croppedImages = updatedFaxData.croppedImages.concat(res.caseDocs.faceSheetData);
      }
      if (res.caseDocs.consentFormData) {
        updatedFaxData.images = updatedFaxData.images.concat(res.caseDocs.consentFormData);
        updatedFaxData.croppedImages = updatedFaxData.croppedImages.concat(res.caseDocs.consentFormData);
      }
      if (res.caseDocs.prePostOpFormData) {
        updatedFaxData.images = updatedFaxData.images.concat(res.caseDocs.prePostOpFormData);
        updatedFaxData.croppedImages = updatedFaxData.croppedImages.concat(res.caseDocs.prePostOpFormData);
      }
      if (res.caseDocs.intraOpFormData) {
        updatedFaxData.images = updatedFaxData.images.concat(res.caseDocs.intraOpFormData);
        updatedFaxData.croppedImages = updatedFaxData.croppedImages.concat(res.caseDocs.intraOpFormData);
      }
    }

    /* eslint-disable */
    let sBillCanvas = await html2canvas(document.getElementById("sbill"));
    updatedFaxData.images = updatedFaxData.images.concat(sBillCanvas.toDataURL());
    updatedFaxData.croppedImages = updatedFaxData.croppedImages.concat(sBillCanvas.toDataURL());
    setFaxData(updatedFaxData)
    /* eslint-enable */
    setShowFaxLoader(false);
  };

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Fax Status Message Toast */}
      {showFaxStatusMsg &&
        <Toast
          id='fax_Status_msg'
          top='41'
          bottom={null}
          headerText='Information'
          headerIcon='fa fa-info-circle'
          bodyText={faxStatusMsg}
          onDismiss={() => {
            setShowFaxStatusMsg(false);
          }}
          showFooter={false}
        />
      }

      {showWarningDialog &&
        <WarningDialog
          show={showWarningDialog}
          title='Close Case Drawer'
          message='Do you want to close without saving case?'
          onCancel={() => setShowWarningDialog(false)}
          onContinue={() => {
            setShowNewCaseDrawer(false);
            setShowWarningDialog(false);
          }}
        />
      }
      {/* Case Drawer */}
      {showCaseDrawer &&
        <CaseDrawer
          caseData={caseData}
          editMode={isCaseDrawerInEditMode}
          onReloadData={getData}
          faceSheetKey={!isCaseDrawerInEditMode ? formS3Key : caseData.faceSheetKey}
          onUploadFaceSheet={handleOnUploadFaceSheet}
          onDeleteFaceSheet={handleOnDeleteFaceSheet}
          onDeleteOtherFacility={() => {
            setCaseData({
              ...caseData,
              otherFacility: null
            });
          }}
          onDeleteOtherSurgeon={(id) => {
            const updatedOtherSurgeon = caseData.otherSurgeon.filter(el => el.id !== id);
            setCaseData({
              ...caseData,
              otherSurgeon: updatedOtherSurgeon
            });
          }}
          onDeleteOtherAnesthesiologist={(id) => {
            const updatedOtherAnesthesiologist = caseData.otherAnesthesiologist.filter(el => el.id !== id);
            setCaseData({
              ...caseData,
              otherAnesthesiologist: updatedOtherAnesthesiologist
            });
          }}
          onDeleteOtherCrna={(id) => {
            const updatedOtherCrna = caseData.otherCrna.filter(el => el.id !== id);
            setCaseData({
              ...caseData,
              otherCrna: updatedOtherCrna
            });
          }}
          onDeleteOtherProcedure={(id) => {
            const updatedOtherSurgicalProcedure = caseData.otherSurgicalProcedure.filter(el => el.id !== id);
            setCaseData({
              ...caseData,
              otherSurgicalProcedure: updatedOtherSurgicalProcedure
            });
          }}
          onClose={(_bool) => {
            if (_bool) {
              setShowWarningDialog(true);
            } else {
              setFormS3Key('');
              setShowCaseDrawer(false);
              setShowCaseUploadDrawer(false);
            }
          }}
        />
      }

      {/* Case Upload Dialog */}
      {showCaseUploadDrawer
        ? <Drawer
          placement="right"
          visible={true}
          toggleDrawer={() => setShowCaseUploadDrawer(false)}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                {/* <i className='mr-2 fa fa-lg fa-user' /> */}
                <div className={css(styles.headerSvgIcon)}>
                  {CasesIcon}
                </div>
                <div className='noselect'>Create New Case</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowCaseUploadDrawer(false);
                }}
              />
            </div>
            {/* Middle Area */}
            <div
              className={css(styles.drawerContentArea)}
              style={{
                height: `${window.innerHeight - 40}px`
              }}
            >
              {!isAnalyzingForm && !isUploadingFile
                ? <div style={{ height: '100%' }}>
                  <FileUploader
                    text='Drag or Select Face Sheet'
                    showOrLineBreak={false}
                    parentRef={fileInputRef}
                    onDrop={handleOnFileDrop}
                    onChange={handleOnFileSelected}
                    height='100%'
                  />
                </div>
                : null
              }
              {isUploadingFile
                ? <div style={{ height: '100%', background: 'black' }}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Face Sheet" />
                </div>
                : null
              }
              {isAnalyzingForm
                ? <div style={{ height: '100%', background: 'black' }}>
                  <Loader iconType="spinnerWithPulse" iconSize="5x" text="Analyzing Face Sheet" />
                </div>
                : null
              }
            </div>
            {/* Footer */}
            <div
              className={`fx pr-2 ${css(styles.drawerFooter)}`}
              style={{ display: isTouchDevice ? 'none' : '' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                isOutlineBtn
                onClick={() => {
                  setShowCaseUploadDrawer(false);
                }}
                style={{
                  height: '32px',
                  width: '69px',
                  padding: '3px 0'
                }}
              >
                Cancel
              </Button>
              <Button
                btnType="primary"
                className="ml-2 fx fx__hv-center"
                onClick={() => {
                  setShowCaseUploadDrawer(false);
                  setShowCaseDrawer(false);
                  resetCaseAnalysisDrawerState();
                  toggleShowCaseAnalysisDrawer(true);
                }}
                style={{
                  height: '32px',
                  width: '200px',
                  padding: '3px 0'
                }}
              >
                Manually Create Case
              </Button>
            </div>
          </div>
        </Drawer>
        : null
      }

      {/** Camera/Cropper Drawer */}
      {showCameraDrawer
        ? <Drawer
          placement="right"
          visible={true}
          toggleDrawer={closeCameraDrawer}
          zIndex="2000"
          width={drawerWidth}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Middle Area */}
            <div
              className={css(styles.drawerContentArea)}
              style={{ position: 'relative', height: `${window.innerHeight - 40}px` }}
            >
              {!isUploadingFile && !showCropper &&
                <CameraContainer
                  images={data.images}
                  onEdit={() => {
                    setShowCropper(true);
                  }}
                  showCancel={data.images.length === 0 && data.croppedImages.length === 0}
                  onManual={() => {
                    setShowCaseUploadDrawer(false);
                    setShowCaseDrawer(false);
                    resetCaseAnalysisDrawerState();
                    toggleShowCaseAnalysisDrawer(true);
                    setShowCropper(false);
                    setShowCameraDrawer(false);
                  }}
                  showManual={data.images.length === 0}
                  onCancel={() => {
                    setShowCropper(false);
                    setShowCameraDrawer(false);
                  }}
                  onSkip={() => {
                    setShowCameraDrawer(false);
                    setShowCaseUploadDrawer(true);
                    setIsAnalyzingForm(false);
                    setIsUploadingFile(true);
                    setShowCaseDrawer(false);
                    resetCaseAnalysisDrawerState();
                    toggleShowCaseAnalysisDrawer(true);
                  }}
                  onScan={(img) => {
                    if (data.isReScan) {
                      setData((prvState) => {
                        const tmpImages = prvState.images.concat();
                        tmpImages[prvState.activeIndex] = img;
                        const tmpCroppedImages = prvState.croppedImages.concat();
                        tmpCroppedImages[prvState.activeIndex] = img;
                        return {
                          ...prvState,
                          images: tmpImages,
                          croppedImages: tmpCroppedImages
                        };
                      });
                    } else {
                      setData((prvState) => ({
                        ...prvState,
                        images: prvState.images.concat(img),
                        croppedImages: prvState.croppedImages.concat(img)
                      }));
                    }
                    setShowCropper(true);
                  }}
                />
              }
              {!isUploadingFile && showCropper &&
                <CropperContainer
                  ref={imgCropper}
                  currentIndex={Math.min(data.activeIndex, data.images.length - 1)}
                  images={data.images}
                  croppedImages={data.croppedImages}
                  loadImage={(idx) => {
                    setData(prvState => ({ ...prvState, activeIndex: idx }));
                  }}
                  keySuffix='init_face_sheet'
                  onRevert={(idx) => {
                    const tmpCroppedImages = data.croppedImages.concat();
                    tmpCroppedImages[idx] = data.images[idx];
                    setData((prvData) => ({
                      ...prvData,
                      croppedImages: tmpCroppedImages
                    }));
                  }}
                  onDelete={(idx) => {
                    setData((prvState) => {
                      const images = prvState.images.concat();
                      images.splice(idx, 1);
                      const croppedImages = prvState.croppedImages.concat();
                      croppedImages.splice(idx, 1);
                      return {
                        ...prvState,
                        images,
                        croppedImages,
                        activeIndex: Math.max(0, images.length - 1)
                      };
                    });
                  }}
                  onClose={() => {
                    setData({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
                    setShowCameraDrawer(false);
                    setShowCropper(false);
                  }}
                  onCancel={() => {
                    setData((prvState) => ({
                      ...data,
                      images: [],
                      croppedImages: []
                    }));
                    setData(prvState => ({ ...prvState, croppedImages: [] }));
                    setShowCropper(false);
                    setShowCaseUploadDrawer(false);
                    setShowCameraDrawer(false);
                    setShowCaseDrawer(false);
                    resetCaseAnalysisDrawerState();
                    toggleShowCaseAnalysisDrawer(true);
                  }}
                  onReScan={() => {
                    setData(prvState => ({ ...prvState, isReScan: true }));
                    setShowCropper(false);
                    console.log('On Re-Scan');
                  }}
                  onScanNext={() => {
                    setShowCropper(false);
                    setData(prvState => ({
                      ...prvState,
                      activeIndex: prvState.activeIndex + 1,
                      isReScan: false
                    }));
                    console.log('On Scan Next');
                  }}
                  onCrop={() => {
                    if (data.isReScan) {
                      setData(prvState => {
                        const { cropper } = imgCropper.current;
                        const tmpCroppedImages = prvState.croppedImages.concat();
                        tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                        return {
                          ...prvState,
                          croppedImages: tmpCroppedImages
                        };
                      });
                    } else {
                      setData(prvState => {
                        const { cropper } = imgCropper.current;
                        const tmpCroppedImages = prvState.croppedImages.concat();
                        tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                        return {
                          ...prvState,
                          croppedImages: tmpCroppedImages
                        };
                      });
                    }
                    console.log('On Crop');
                  }}
                  onFinish={() => {
                    handleOnClickCropAccept();
                    console.log('On Finish!');
                  }}
                />
              }
            </div>
            {/* Footer */}
            <div
              className={`fx pr-2 ${css(styles.drawerFooter)}`}
              style={{ display: 'none', justifyContent: 'center', height: '88px' }}
            >
              {data.images.length === 0
                ? <div>
                  <Button
                    btnType="primary"
                    className="ml-2 fx fx__hv-center"
                    onClick={() => {
                      setData((prvState) => ({
                        ...data,
                        images: prvState.images.concat(camera.current.takePhoto())
                      }));
                      setShowCropper(true);
                    }}
                    style={{
                      height: '32px',
                      width: '150px',
                      padding: '3px 0'
                    }}
                  >
                    Take Picture
                  </Button>
                  <Button
                    btnType="primary"
                    className="ml-2 fx fx__hv-center"
                    onClick={() => {
                      setShowCaseUploadDrawer(false);
                      setShowCameraDrawer(false);
                      setShowCaseDrawer(false);
                      resetCaseAnalysisDrawerState();
                      toggleShowCaseAnalysisDrawer(true);
                    }}
                    style={{
                      height: '32px',
                      width: '200px',
                      padding: '3px 0'
                    }}
                  >
                    Manually Create Case
                  </Button>
                </div>
                : <>
                  {/* Rescan */}
                  <Button
                    btnType='primary'
                    isOutlineBtn
                    className='mr-2'
                    style={{ width: '80px' }}
                    onClick={() => {
                      setData((prvState) => ({
                        ...data,
                        images: []
                      }));
                      setShowCropper(false);
                    }}
                  >
                    ReScan
                  </Button>
                  {/* Crop */}
                  {/* <Button
                        className='mr-2'
                        style={{ width: '80px' }}
                        onClick={() => {
                          setShowCropper(true);
                        }}
                      >
                        Crop
                      </Button> */}
                  {/* Accept */}
                  <Button
                    btnType='primary'
                    style={{ width: '80px' }}
                    onClick={handleOnClickCropAccept}
                  >
                    Accept
                  </Button>
                </>
              }
            </div>
          </div>
        </Drawer>
        : null
      }

      {/* Create Case Drawer / Manually Create Case Drawer */}
      {showCaseAnalysisDrawer
        ? <Drawer
          placement="right"
          visible={true}
          toggleDrawer={closeCaseAnalysisDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div
            className={css(styles.drawerContainer)}
            style={{ position: 'relative' }}
          >
            {/* Facility MaskedDataInput */}
            <MaskedDataInput
              visible={showFacilityDataInput}
              apiSearchPath='users/searchFacilities'
              title='Search Facilities'
              onClose={() => setShowFacilityDataInput(false)}
              apiDataKey='facilities'
              noneObject={{
                _id: '11111Fac',
                npi: 0,
                avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg',
                facilityName: ''
              }}
              propVar='facilityName'
              menuItemTemplate={(el, highlight, noneObj = false) => {
                return (
                  <div
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    key={el._id}
                    onClick={() => {
                      setFacilityList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          facility: el._id,
                          facilityName: el.facilityName,
                          npi: el.npi,
                          text: `${el.facilityName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: el.facilityName,
                          caseRelation: ''
                        });
                      });
                      setShowFacilityDataInput(false);
                    }}
                  >
                    {/* Avatar */}
                    <div className={css(styles.menuItemAvatarContainer)}>
                      <img
                        src={el.avatarImageUrl}
                        alt='Facility Avatar'
                        className={css(styles.menuItemAvatarImg)}
                      />
                    </div>
                    {/* Facility Name */}
                    <div className='ml-2'>
                      <div>{getHighlightedText(el.facilityName, highlight)}</div>
                      <div>{el.taxonomy}</div>
                    </div>
                  </div>
                );
              }}
            />
            {/* Surgeons MaskedDataInput */}
            <MaskedDataInput
              visible={showSurgeonsDataInput}
              apiSearchPath='users/searchMedProsSurgeons'
              title='Search Surgeons'
              onClose={() => setShowSurgeonsDataInput(false)}
              apiDataKey='users'
              noneObject={{
                _id: '22222Surg',
                lastName: '',
                firstName: '',
                icon: '',
                avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
                npi: 0
              }}
              propVar='lastName'
              menuItemTemplate={(el, highlight, noneObj = false) => {
                return (
                  <div
                    key={el._id}
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setSurgeonsList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: !noneObj ? `${el.lastName}, ${el.firstName}` : `${el.lastName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: !noneObj ? `${el.lastName}, ${el.firstName}` : `${el.lastName}`,
                          npi: el.npi,
                          caseRelation: 'Surgeon'
                        });
                      });
                      setShowSurgeonsDataInput(false);
                    }}
                  >
                    {/* Avatar */}
                    <div className={css(styles.menuItemAvatarContainer)}>
                      <img
                        src={el.avatarImageUrl}
                        alt='Surgeon Avatar'
                        className={css(styles.menuItemAvatarImg)}
                      />
                    </div>
                    {/* Surgeon Name && Specialty */}
                    <div className='ml-2'>
                      {noneObj
                        ? <div>{el.lastName}</div>
                        : <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, highlight)}</div>
                      }
                      <div>{el.specialty}</div>
                    </div>
                  </div>
                );
              }}
            />
            {/* Procedure MaskedDataInput */}
            <MaskedDataInput
              visible={showProceduresDataInput}
              apiSearchPath='cpt/searchCpt2'
              title='Search Procedures'
              onClose={() => setShowProceduresDataInput(false)}
              apiDataKey='cptCodes'
              noneObject={{
                _id: '33333Procedure',
                code: 'N/A',
                description: '',
                longDescription: '',
                shortDescription: '',
                npi: 0
              }}
              propVar='shortDescription'
              menuItemTemplate={(el, highlight, noneObj = false) => {
                return (
                  <div
                    key={el._id}
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setProceduresList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.shortDescription}`,
                          code: el.code,
                          description: `${el.shortDescription}`,
                          npi: el.npi
                        });
                      });
                      setShowProceduresDataInput(false);
                    }}
                  >
                    <div className='ml-2'>
                      {!noneObj
                        ? <>
                          <div style={{ fontWeight: 'bold' }}>
                            CPT CODE: {el.code}
                          </div>
                          <div>{getHighlightedText(el.longDescription, highlight)}</div>
                        </>
                        : <div>{el.shortDescription}</div>
                      }
                    </div>
                  </div>
                );
              }}
            />
            {/* Anesthesiologists MaskedDataInput */}
            <MaskedDataInput
              visible={showAnesthesiologistsDataInput}
              apiSearchPath='users/searchMedProsAnesthesiologists'
              title='Search Anesthesiologists'
              onClose={() => setShowAnesthesiologistsDataInput(false)}
              apiDataKey='users'
              noneObject={{
                _id: '22222Surg',
                lastName: '',
                firstName: '',
                icon: '',
                avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
                npi: 0
              }}
              propVar='lastName'
              menuItemTemplate={(el, highlight, noneObj = false) => {
                return (
                  <div
                    key={el._id}
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setAnesthesiologistsList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: el.npi,
                          caseRelation: 'Anesthesiologist'
                        });
                      });
                      setShowAnesthesiologistsDataInput(false);
                    }}
                  >
                    {/* Avatar */}
                    <div className={css(styles.menuItemAvatarContainer)}>
                      <img
                        src={el.avatarImageUrl}
                        alt='Anesthesiologist Avatar'
                        className={css(styles.menuItemAvatarImg)}
                      />
                    </div>
                    {/* Anesthesiologist Name && Specialty */}
                    <div className='ml-2'>
                      {noneObj
                        ? <div>{el.lastName}</div>
                        : <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, highlight)}</div>
                      }
                      <div>{el.specialty}</div>
                      {/* <div>{`${el.lastName}, ${el.firstName}`}</div>
                        <div>{el.specialty}</div> */}
                    </div>
                  </div>
                );
              }}
            />
            {/* CRNAs MaskedDataInput */}
            <MaskedDataInput
              visible={showCrnasDataInput}
              apiSearchPath='users/searchMedProsCrnas'
              title='Search CRNA(s)/ CAA(s)'
              onClose={() => setShowCrnasDataInput(false)}
              apiDataKey='users'
              noneObject={{
                _id: '22222Surg',
                lastName: '',
                firstName: '',
                icon: '',
                avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
                npi: 0
              }}
              propVar='lastName'
              menuItemTemplate={(el, highlight, noneObj = false) => {
                return (
                  <div
                    key={el._id}
                    className={`fx fx__v-center ${css(styles.menuItem)}`}
                    onClick={() => {
                      setCrnasList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: el.npi,
                          caseRelation: 'CRNA'
                        });
                      });
                      setShowCrnasDataInput(false);
                    }}
                  >
                    {/* Avatar */}
                    <div className={css(styles.menuItemAvatarContainer)}>
                      <img
                        src={el.avatarImageUrl}
                        alt='Anesthesiologist Avatar'
                        className={css(styles.menuItemAvatarImg)}
                      />
                    </div>
                    {/* CRNA Name & Specialty */}
                    <div className='ml-2'>
                      {noneObj
                        ? <div>{el.lastName}</div>
                        : <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, highlight)}</div>
                      }
                      <div>{el.specialty}</div>
                      {/* <div>{`${el.lastName}, ${el.firstName}`}</div>
                        <div>{el.specialty}</div> */}
                    </div>
                  </div>
                );
              }}
            />
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                {/* <i className='mr-2 fa fa-lg fa-user' /> */}
                <div className={css(styles.headerSvgIcon)}>
                  {CasesIcon}
                </div>
                <div className='noselect'>Create New Case</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={closeCaseAnalysisDrawer}
              />
            </div>
            {/* Middle Area */}
            <div
              style={{
                height: 'calc(100% - 80px)',
                overflow: 'scroll',
                position: 'relative',
                padding: '16px',
                fontSize: '14px'
              }}
            >
              {/* Date of Surgery, InPt/ Out Pt */}
              <div className='fx fx__v-center mt-2'>
                <i className='mr-2 fa fa-calendar' />
                <div>DOS</div>
                <div className='ml-2'>
                  <DatePicker
                    left={-30}
                    right={0}
                    date={caseDosDate}
                    month={caseDosMonth}
                    year={caseDosYear}
                    onChangeDate={(year, month, date) => {
                      setCaseDosYear(year);
                      setCaseDosMonth(month);
                      setCaseDosDate(date);
                    }}
                  />
                </div>
                <div
                  className='ml-4'
                  style={{
                    color: (casePatientType === 'InPt' || casePatientType === 'OutPt') ? '' : salmon[500]
                  }}
                >
                  <input
                    className={css(styles.caseFieldCheckbox)}
                    type='radio'
                    checked={casePatientType === 'InPt'}
                    onChange={(e) => {
                      const { checked } = e.target;
                      if (checked) {
                        setCasePatientType('InPt');
                      }
                    }}
                  />
                  &nbsp;In Pt
                </div>
                <div
                  className='ml-2'
                  style={{
                    color: (casePatientType === 'InPt' || casePatientType === 'OutPt') ? '' : salmon[500]
                  }}
                >
                  <input
                    className={css(styles.caseFieldCheckbox)}
                    type='radio'
                    checked={casePatientType === 'OutPt'}
                    onChange={(e) => {
                      const { checked } = e.target;
                      if (checked) {
                        setCasePatientType('OutPt');
                      }
                    }}
                  />
                  &nbsp;Out Pt
                </div>
              </div>
              {/* ------------ Facility ------------ */}
              <CaseField
                heading='Facility'
                showPlusIcon={facilityList.length === 0}
                showIsRequired={facilityList.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  setShowFacilityDataInput(true);
                }}
              />
              {/* Facility Cards */}
              <div>
                {facilityCards}
              </div>
              {/* ------------ Surgeons ------------ */}
              <CaseField
                heading='Surgeon(s)'
                showPlusIcon
                showIsRequired={surgeonsList.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  setSearchTerm('');
                  setCurrentInput('Surgeon(s)');
                  setShowSurgeonsDataInput(true);
                }}
              />
              <div>
                {surgeonCards}
              </div>
              {/* ------------ Procedure(s) ------------ */}
              <CaseField
                heading='Procedure(s)'
                showPlusIcon
                showIsRequired={proceduresList.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  setShowProceduresDataInput(true);
                }}
              />
              <div>
                {surgicalProcedureCards}
              </div>
              {/* ------------ Anesthesiologist(s) ------------ */}
              <div>
                <CaseField
                  heading='Anesthesiologist(s)'
                  showPlusIcon={!isNoneAnesthesiologist}
                  className='mt-4'
                  handleOnClickPlusIcon={() => {
                    setShowAnesthesiologistsDataInput(true);
                  }}
                />
                {anesthesiologistsList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <input
                      type='checkbox'
                      className={`mr-1 ${css(styles.caseFieldCheckbox)}`}
                      value={isNoneAnesthesiologist}
                      onChange={(e) => {
                        e.stopPropagation();
                        setIsNoneAnesthesiologist(!isNoneAnesthesiologist);
                      }}
                    />
                    <span
                      style={{ color: !isNoneAnesthesiologist ? salmon[500] : '' }}
                    >
                      Check Box If &#34;None&#34;
                    </span>
                  </div>
                }
              </div>
              <div>
                {anesthesiologistCards}
              </div>
              {/* ------------ CRNA(s) ------------ */}
              <div>
                <CaseField
                  heading='CRNA(s)/ CAA(s)'
                  showPlusIcon={!isNoneCrna}
                  className='mt-4'
                  handleOnClickPlusIcon={() => {
                    setShowCrnasDataInput(true);
                  }}
                />
                {crnasList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <input
                      type='checkbox'
                      className={`mr-1 ${css(styles.caseFieldCheckbox)}`}
                      value={isNoneCrna}
                      onChange={(e) => {
                        e.stopPropagation();
                        setIsNoneCrna(!isNoneCrna);
                      }}
                    />
                    <span style={{ color: !isNoneCrna ? salmon[500] : '' }}>
                      Check Box If &#34;None&#34;
                    </span>
                  </div>
                }
              </div>
              <div>
                {crnaCards}
              </div>
            </div>
            {/* Footer */}
            <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                isOutlineBtn
                onClick={closeCaseAnalysisDrawer}
                style={{
                  height: '32px',
                  width: '69px',
                  padding: '3px 0'
                }}
              >
                Cancel
              </Button>
              <Button
                btnType="primary"
                className="ml-2 fx fx__hv-center"
                isDisabled={!canCreateCase}
                onClick={handleOnClickCreateCase}
                style={{
                  height: '32px',
                  width: '115px',
                  padding: '3px 0'
                }}
              >
                Create Case
              </Button>
            </div>
          </div>
        </Drawer>
        : null
      }

      {showNewCaseDrawer &&
        <NewCaseDrawer
          onClose={(_bool = false) => {
            if (_bool) {
              setShowWarningDialog(true);
            } else {
              setShowNewCaseDrawer(false);
            }
          }}
          reloadData={() => {
            setShowNewCaseDrawer(false);
            getData();
          }}
        />
      }

      {/* ---- Delete Case Warning ---- */}
      {showDeleteCaseWarning &&
        <WarningDialog
          show={showDeleteCaseWarning}
          title='Delete Case'
          message='Do you really want to delete case?'
          onCancel={() => setShowDeleteCaseWarning(false)}
          onContinue={() => {
            handleOnClickDeleteCase();
          }}
        />
      }

      {/* ---------------- Fax Drawer ---------------- */}
      {showFaxDrawer
        ? <Drawer
          placement="right"
          visible={true}
          toggleDrawer={() => setShowFaxDrawer(false)}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div
            className={css(styles.drawerContainer)}
            style={{ position: 'relative' }}
          >
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <div className={css(styles.headerSvgIcon)}>
                  <svg
                    width='24'
                    height='24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0ZM16.8 9h.8c1.328 0 2.4 1.072 2.4 2.4v6.4H8.176c-.44.488-1.064.8-1.776.8A2.397 2.397 0 0 1 4 16.2v-5.6c0-1.328 1.072-2.4 2.4-2.4.712 0 1.336.312 1.776.8H8.8V5h8v4ZM6.4 17c.44 0 .8-.36.8-.8v-5.6c0-.44-.36-.8-.8-.8-.44 0-.8.36-.8.8v5.6c0 .44.36.8.8.8Zm8.8-10.4h-4.8V9h4.8V6.6Zm-6.4 4v5.6h9.6v-4.8c0-.44-.36-.8-.8-.8H8.8Zm6.4 1.6a.8.8 0 1 1-1.6 0 .8.8 0 0 1 1.6 0Zm1.6.8a.8.8 0 1 0 0-1.6.8.8 0 0 0 0 1.6Zm-1.6 1.6a.8.8 0 1 1-1.6 0 .8.8 0 0 1 1.6 0Zm1.6.8a.8.8 0 1 0 0-1.6.8.8 0 0 0 0 1.6Zm-7.2-4h3.2v4H9.6v-4Z'
                      fill='#96C8FF'
                    />
                  </svg>
                </div>
                <div className='noselect'>Send Fax</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowFaxDrawer(false);
                }}
              />
            </div>
            {/* Middle Area */}
            {!showFaxLoader
              ? <div
                className={css(styles.drawerContentArea)}
                style={{
                  height: `${window.innerHeight - 40}px`,
                  padding: '16px'
                }}
              >
                {/* Recipient Name */}
                <div>
                  <TextInputWithLabel
                    value={recipientName}
                    titleText="Recipients Name"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={342}
                    placeholder='Required'
                    onChange={handleOnEditRecipientName}
                  />
                </div>
                {/* Senders Fax Number */}
                <div
                  className='mt-4 fx'
                  style={{ alignItems: 'flex-end' }}
                >
                  <div className='mr-2'>
                    +1
                  </div>
                  <TextInputWithLabel
                    value={recipientFax}
                    titleText="Recipients Fax#"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={316}
                    placeholder='Required'
                    onChange={handleOnEditRecipientFax}
                  />
                </div>
                {/* Error msg */}
                <div style={{ padding: '8px', paddingLeft: '26px', color: '#FF6060' }}>
                  {!isRecipientFaxPristine && !isMobilePhone(recipientFax, 'en-US') ? 'Invalid fax number' : ''}
                </div>

                {/* Documents to Fax */}
                <FileCard
                  fileName='Fax Attachment'
                  numberOfPages={faxDocs.length}
                  className='mt-4'
                  fileKey='anesthesiaCase.pdf'
                  isLoading={false}
                  onDelete={() => { console.log('Delete function not implemented yet!'); }}
                  onEdit={() => { console.log('Edit function not implemented yet!'); }}
                  onView={handleOnClickViewFaxDocs}
                />
                {/* Fax Cover Message */}
                <div style={{ flexGrow: '1' }} className='mt-4'>
                  <textarea
                    placeholder='Type message...'
                    className={css(styles.faxCoverMessage)}
                    value={faxCoverMessage}
                    onChange={handleOnEditFaxCoverMessage}
                  />
                </div>
              </div>
              : <Loader
                iconType={faxStatus === FAX.preparing ? 'circle' : 'spinnerWithPulse'}
                iconSize="5x"
                text={faxLoaderMessage}
              />
            }
            {/* Superbill */}
            {/* <div
              style={{
                position: 'absolute',
                top: '0',
                left: '-5000px',
                width: '1010px',
                height: '1020px'
              }}
            >
              {popupMenuIndex > -1 &&
                <Superbill
                  caseData={cases[popupMenuIndex]}
                  patients={[cases[popupMenuIndex].patientId]}
                />
              }
            </div> */}
            {/* Footer */}
            <div
              className={`fx pr-2 ${css(styles.drawerFooter)}`}
            // style={{ display: isTouchDevice ? 'none' : '' }}
            >
              {/* Send Fax Button */}
              <Button
                btnType="primary"
                isDisabled={!canSendFax}
                className="ml-2 fx fx__hv-center"
                style={{
                  height: '32px',
                  width: '120px',
                  padding: '3px 0'
                }}
                onClick={handleOnClickSendFax}
              >
                <i className='fa fa-paper-plane mr-2' />
                Fax Case
              </Button>
            </div>
          </div>
        </Drawer>
        : null
      }

      {/* View Fax Documents */}
      {showFaxDocs &&
        <Drawer
          placement='right'
          visible={showFaxDocs}
          toggleDrawer={() => setShowFaxDocs(false)}
          zIndex="2200"
          showMask={false}
          width={window.innerWidth}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Drawer Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <div className='ml-2'>Fax Attachment</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowFaxDocs(false);
                }}
              />
            </div>
            {/* Toolbar */}
            <div className={css(styles.faxDocsToolbar)}>
              {/* Toolbar left section */}
              <div className={css(styles.faxDocsToolbarLeft)}>
                {/* Thumbnails drawer icon */}
                <div
                  style={{
                    position: 'relative',
                    paddingTop: '6px'
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="30"
                    fill="none"
                    className={css(styles.toolbarIcon)}
                    onClick={() => setShowFaxDocsThumbnailDrawer(!showFaxDocsThumbnailDrawer)}
                  >
                    <path
                      fillRule="evenodd"
                      d="M29.285 0H2.215C.99 0 0 1.258 0 2.813v24.375C0 28.741.99 30 2.215 30h27.07c1.224 0 2.215-1.258 2.215-2.813V2.813C31.5 1.258 30.51 0 29.285 0ZM12 27H3V3h9v24Zm3 0h13.5V3c0 .085-.01.142-.022.142H15.022C15.009 3.142 15 3.071 15 3v24Zm-4.5-16.5h-6v-6h6v6Zm-6 7.5h6v-6h-6v6Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {/* Badge */}
                  <div
                    className={css(styles.badge)}
                    onClick={() => setShowFaxDocsThumbnailDrawer(!showFaxDocsThumbnailDrawer)}
                  >
                    {faxDocs.length}
                  </div>
                </div>
                {/* Page Selector */}
                <div className={`ml-2 ${css(styles.pageSelector)}`}>
                  {/* Go Back */}
                  <div
                    className={css(styles.pageSelectorBtn)}
                    style={{
                      fontSize: '18px',
                      borderTopLeftRadius: '5px',
                      borderBottomLeftRadius: '5px',
                      color: '#5080B0'
                    }}
                    onClick={() => {
                      setSelectedFaxDocument(Math.max(0, selectedFaxDocument - 1));
                    }}
                  >
                    <i className='fa fa-minus-circle' />
                  </div>
                  {/* Current Page */}
                  <div className={css(styles.pageSelectorPageNumberDisplay)}>
                    {selectedFaxDocument + 1}
                  </div>
                  {/* Go Forward */}
                  <div
                    className={css(styles.pageSelectorBtn)}
                    style={{
                      color: '#5080B0',
                      fontSize: '18px',
                      borderTopRightRadius: '5px',
                      borderBottomRightRadius: '5px'
                    }}
                    onClick={() => {
                      setSelectedFaxDocument(Math.min(faxDocs.length - 1, selectedFaxDocument + 1));
                    }}
                  >
                    <i className='fa fa-plus-circle' />
                  </div>
                </div>
              </div>
            </div>
            {/* Drawer Container */}
            <div
              className={css(styles.faxDocsDrawerContentArea)}
              style={{ position: 'relative' }}
            >
              {/* Thumbnails */}
              {showFaxDocsThumbnailDrawer &&
                <div
                  className={css(styles.thumbnailDrawer)}
                  style={{
                    top: '0',
                    left: '0',
                    height: '100%'
                  }}
                >
                  {faxDocs.map((item, idx) => (
                    <div
                      key={item.id}
                      className='mt-2'
                      style={{
                        background: blue[900],
                        opacity: item.chosen ? '0.5' : '1',
                        border: idx === selectedFaxDocument ? `4px solid ${blue[400]}` : `2px solid ${gray[300]}`,
                        width: '100px'
                      }}
                    >
                      {/* Thumbnail */}
                      <div
                        style={{
                          minHeight: '126px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <img
                          onClick={() => {
                            setSelectedFaxDocument(idx);
                          }}
                          src={item.imageUrl}
                          style={{
                            maxHeight: '108px',
                            maxWidth: '84px'
                          }}
                        />
                      </div>
                      {/* Page Number */}
                      <div
                        style={{
                          background: blue[900],
                          height: '20px',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          paddingTop: '2px',
                          paddingLeft: '4px'
                        }}
                      >
                        {`Page ${idx + 1}`}
                      </div>
                    </div>
                  ))}
                </div>
              }
              {/* Selected Fax Doc */}
              {faxDocs.length > 0 &&
                <div
                  style={{
                    width: '100%',
                    height: 'calc(100% - 40px)',
                    background: 'black',
                    textAlign: 'center',
                    overflow: 'auto'
                  }}
                  className='fx fx__hv-center'
                >
                  <img
                    src={faxDocs[selectedFaxDocument].imageUrl}
                    className={css(styles.faxDocImgAutosize)}
                  />
                </div>
              }
            </div>
            {/* Drawer Footer */}
            <div className={css(styles.faxDocsDrawerFooter)}>
              <Button
                btnType='primary'
                onClick={() => setShowFaxDocs(false)}
                isOutlineBtn
                className='mr-2'
              >
                Close
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {isDownloadingCaseDocs &&
        <Loader
          iconType="cog"
          iconSize="5x"
          text="Downloading case docuements PDF file."
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            zIndex: '5000',
            background: 'black',
            opacity: '0.5'
          }}
        />
      }

      {/* Grid toolbar and users data grid */}
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Grid toolbar - right aligned buttons */}
          <div className='fx'>
            {/* Cards grid */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickToggleGrid}
            >
              <i className={`fa fa-lg ${showCardsGrid ? 'fa-bars' : 'fa-th-large'}`} />
            </div>
            {/* Add case button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={() => { setShowNewCaseDrawer(true); }}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div>
            {/* Download */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-cloud-download" />
            </div>
            {/* Trash bin */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-trash" />
            </div>
            {/* Checkbox */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-check-square" />
            </div>
            {/* Reset button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
            {/* Vertical ellipsis */}
            <div
              className={css(styles.gridToolbarIcon)}
              style={{ fontSize: '14px' }}
            >
              <span className='fa-stack'>
                <i className='fa fa-circle fa-stack-2x' />
                <i
                  className='fa fa-ellipsis-v fa-stack-1x'
                  style={{ color: gray[700] }}
                />
                {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
              </span>
            </div>
          </div>
        </div>
        {/* Grid Ellipsis Menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Edit Case Menu Item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                // onClick={handleOnClickEditCase}
                onClick={handleOnClickEditCaseOldDashboard}
              >
                Edit Case
              </div>
              {/* Delete record menu item (Loading old dashboard for testing) */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => setShowDeleteCaseWarning(true)}
              >
                Delete Case
              </div>
              {/* Fax Case */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickFaxCase}
              >
                Fax Case
              </div>
              {/* Download Case */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickDownloadCase}
              >
                Download Case
              </div>
            </div>
          </PopupMenu>
        </div>
        {/* Cases Data Grid */}
        {!showCardsGrid
          ? <DataGrid
            loader={showLoader}
            icons={ICONS}
            headerDefs={HEADER_DEFS}
            rowData={gridData}
            filters={{}}
            gridWidth={gridWidth}
            gridHeight={gridHeight}
            gridHeaderHeight={48}
            gridFooterHeight={0}
            gridContainerHeight='730px'
            rowHeight={64}
            rowsPerPage={DOCS_PER_PAGE}
            defaultCellStyle={DEFAULT_CELL_STYLE}
            showFooter={false}
            showPager
            showEllipsisCol
            enableHover
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            isMobileLayout={isMobileLayout}
            onClickCell={handleOnClickCell}
            onApplyFilter={() => { console.warn('Handler not set for onApplyFilter prop.'); }}
            onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
            onLoadPage={(page) => {
              // console.warn('Handler not set for onLoadPage prop.');
              setCurrentPage(page);
            }}
            onColFilterToggle={() => { console.warn('Handler not set for onColFilterToggle prop.'); }}
            onFilterTypeUpdate={() => { console.warn('Handler not set for onFilterTypeUpdate prop.'); }}
            onColPinChange={() => { console.warn('Handler not set for onColPinChange prop.'); }}
            onResize={() => { console.warn('Handler not set for onResize prop.'); }}
            onPopupMenu={handleOnPopupMenu}
            onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
            onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
            onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
            onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
            onSort={() => { console.warn('Handler not set for onSort prop.'); }}
            onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
          />
          : <CardsGrid
            rowData={gridData}
            loader={showLoader}
            gridWidth={gridWidth}
            gridHeight={gridHeight}
            style={{ height: 'calc(100% - 73px)', overflowY: 'auto' }}
            cardHeight='120px'
            cardWidth='320px'
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            onLoadPage={() => { console.warn('Handler not set for onLoadPage prop.'); }}
            cardRenderer={(data) => {
              return (
                <div
                  style={{
                    background: gray[600],
                    margin: '4px 0',
                    padding: '4px 0',
                    height: '120px',
                    display: 'flex',
                    flexFlow: 'column',
                    justifyContent: 'center'
                  }}
                >
                  {/* Facility */}
                  <div className='fx'>
                    <div className={css(styles.headerSection)}>Facility</div>
                    <div className={css(styles.headerSectionText)}>{data.facilityName}</div>
                  </div>
                  {/* Patient */}
                  <div className='fx'>
                    <div className={css(styles.headerSection)}>Pt Name</div>
                    <div className={`fx ${css(styles.headerSectionText)}`}>
                      {/* {`${patLastName.toLowerCase()}, ${patFirstName.toLowerCase()} (${Math.round(patientAge)}y${patSex}, ${new Date(Date.parse(patDob)).toLocaleDateString()})`} */}
                      {data.patientSummary}
                    </div>
                  </div>
                  {/* Surgeon */}
                  <div className='fx'>
                    <div className={css(styles.headerSection)}>Surgeon</div>
                    <div className={css(styles.headerSectionText)}>{data.surgeons}</div>
                  </div>
                  {/* Procedure */}
                  <div className='fx'>
                    <div className={css(styles.headerSection)}>Procedure</div>
                    <div className={css(styles.headerSectionText)}>{data.surgicalProcedures}</div>
                  </div>
                  {/* DOS & InPt/ OutPt */}
                  <div className='fx'>
                    <div className={css(styles.headerSection)}>DOS</div>
                    <div className={`fx ${css(styles.headerSectionText)}`}>
                      <div>
                        <DatePicker
                          left={-42}
                          right={0}
                          isDisabled
                          date={data.dos.getDate()}
                          month={data.dos.getMonth()}
                          year={data.dos.getFullYear()}
                          isPrisitine={false}
                          onChangeDate={(year, month, date) => { }}
                        />
                      </div>
                      <div className='fx'>
                        <div className='ml-4'>
                          <RadioButton
                            text='OutPt'
                            isSelected={data.patientType === 'OutPt'}
                            onSelectionChanged={() => { }}
                          />
                        </div>
                        <div className='ml-4'>
                          <RadioButton
                            text='InPt'
                            isSelected={data.patientType === 'InPt'}
                            onSelectionChanged={() => { }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        }
      </div>
    </div>
  );
});

export default CasesDataGrid;
